import {
  createFillInGapExerciseFingerprint,
  hasExactlyOneFillInGapBlank,
} from "@/lib/fill-in-gap-exercise";
import { createLogger } from "@/lib/logger";
import {
  getTranslationLanguageCodeFromName,
  resolveTranslationLanguageCode,
} from "@/lib/translation-language";
import { useCallback, useMemo, useRef, useState } from "react";
import { ExerciseData, ExerciseQueue, ExerciseQueueHookReturn } from "../types";

const logger = createLogger("useExerciseQueue");

const BATCH_SIZE = 3;
const MIN_QUEUE_SIZE = 1;
const MAX_SEEN_EXERCISES_PER_QUEUE = 100;

function isExerciseForScope(
  exercise: ExerciseData,
  language: string,
  languageCode: string,
  translationLanguageCode: string
): boolean {
  return (
    exercise.language === language &&
    exercise.languageCode === languageCode &&
    exercise.translationLanguageCode === translationLanguageCode &&
    hasExactlyOneFillInGapBlank(exercise.content)
  );
}

export const useExerciseQueue = (
  fromLanguageName?: string,
  translationLanguageCode?: string
): ExerciseQueueHookReturn => {
  const [exerciseQueue, setExerciseQueue] = useState<ExerciseQueue>({});
  const [isGeneratingBatch, setIsGeneratingBatch] = useState(false);
  const exerciseQueueRef = useRef<ExerciseQueue>({});
  const inFlightBatchesRef = useRef<Map<string, Promise<ExerciseData[]>>>(new Map());
  const activeBatchCountRef = useRef(0);
  const seenFingerprintsRef = useRef<Map<string, Set<string>>>(new Map());

  const legacyTranslationCode = getTranslationLanguageCodeFromName(fromLanguageName);
  const translationPreference = translationLanguageCode || legacyTranslationCode;
  const translationPreferenceRef = useRef(translationPreference);
  translationPreferenceRef.current = translationPreference;
  const getTranslationCode = useCallback(
    (languageCode: string) =>
      resolveTranslationLanguageCode({
        targetLanguageCode: languageCode,
        preferredLanguageCode: translationPreference,
      }),
    [translationPreference]
  );
  const getQueueKey = useCallback(
    (language: string, languageCode: string, level: string) =>
      `${language}-${languageCode}-${level}-${getTranslationCode(languageCode)}`,
    [getTranslationCode]
  );

  const replaceQueueForKey = useCallback((queueKey: string, exercises: ExerciseData[]) => {
    const nextQueue = {
      ...exerciseQueueRef.current,
      [queueKey]: exercises,
    };
    exerciseQueueRef.current = nextQueue;
    setExerciseQueue(nextQueue);
  }, []);

  const rememberFingerprint = useCallback((queueKey: string, fingerprint: string): boolean => {
    let seen = seenFingerprintsRef.current.get(queueKey);
    if (!seen) {
      seen = new Set<string>();
      seenFingerprintsRef.current.set(queueKey, seen);
    }
    if (seen.has(fingerprint)) return false;
    seen.add(fingerprint);
    while (seen.size > MAX_SEEN_EXERCISES_PER_QUEUE) {
      const oldest = seen.values().next().value as string | undefined;
      if (!oldest) break;
      seen.delete(oldest);
    }
    return true;
  }, []);

  const appendUniqueExercises = useCallback(
    (queueKey: string, exercises: ExerciseData[]): ExerciseData[] => {
      const uniqueExercises = exercises.filter((exercise) =>
        rememberFingerprint(queueKey, createFillInGapExerciseFingerprint(exercise))
      );
      if (uniqueExercises.length === 0) return [];
      replaceQueueForKey(queueKey, [
        ...(exerciseQueueRef.current[queueKey] || []),
        ...uniqueExercises,
      ]);
      return uniqueExercises;
    },
    [rememberFingerprint, replaceQueueForKey]
  );

  const generateExerciseBatch = useCallback(
    (language: string, languageCode: string, level: string): Promise<ExerciseData[]> => {
      const queueKey = getQueueKey(language, languageCode, level);
      const requestedTranslationCode = getTranslationCode(languageCode);
      const inFlight = inFlightBatchesRef.current.get(queueKey);
      if (inFlight) return inFlight;

      activeBatchCountRef.current += 1;
      setIsGeneratingBatch(true);

      const request = (async () => {
        try {
          const response = await fetch("/api/generate-exercises-batch", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              language,
              languageCode,
              level,
              fromLanguageName,
              translationLanguageCode: requestedTranslationCode,
              count: BATCH_SIZE,
            }),
          });

          if (!response.ok) {
            throw new Error(`Failed to generate exercise batch (${response.status})`);
          }

          const data = (await response.json()) as { exercises?: ExerciseData[] };
          const activeTranslationCode = resolveTranslationLanguageCode({
            targetLanguageCode: languageCode,
            preferredLanguageCode: translationPreferenceRef.current,
          });
          if (activeTranslationCode !== requestedTranslationCode) return [];
          const validExercises = (data.exercises || []).filter((exercise) =>
            isExerciseForScope(exercise, language, languageCode, requestedTranslationCode)
          );
          return appendUniqueExercises(queueKey, validExercises);
        } catch (error) {
          logger.error("Error generating exercise batch:", { error });
          return [];
        } finally {
          inFlightBatchesRef.current.delete(queueKey);
          activeBatchCountRef.current = Math.max(0, activeBatchCountRef.current - 1);
          setIsGeneratingBatch(activeBatchCountRef.current > 0);
        }
      })();

      inFlightBatchesRef.current.set(queueKey, request);
      return request;
    },
    [appendUniqueExercises, fromLanguageName, getQueueKey, getTranslationCode]
  );

  const takeNextQueuedExercise = useCallback(
    (queueKey: string, language: string, languageCode: string): ExerciseData | null => {
      const currentQueue = exerciseQueueRef.current[queueKey] || [];
      const validExercises = currentQueue.filter((exercise) =>
        isExerciseForScope(exercise, language, languageCode, getTranslationCode(languageCode))
      );
      const nextExercise = validExercises[0] || null;
      replaceQueueForKey(queueKey, nextExercise ? validExercises.slice(1) : validExercises);
      return nextExercise;
    },
    [getTranslationCode, replaceQueueForKey]
  );

  const getNextExercise = useCallback(
    async (
      selectedLanguage: string,
      selectedLanguageCode: string,
      currentLevel: string
    ): Promise<ExerciseData | null> => {
      const queueKey = getQueueKey(selectedLanguage, selectedLanguageCode, currentLevel);
      let nextExercise = takeNextQueuedExercise(queueKey, selectedLanguage, selectedLanguageCode);

      if (!nextExercise) {
        await generateExerciseBatch(selectedLanguage, selectedLanguageCode, currentLevel);
        nextExercise = takeNextQueuedExercise(queueKey, selectedLanguage, selectedLanguageCode);
      }

      const remaining = exerciseQueueRef.current[queueKey]?.length || 0;
      if (nextExercise && remaining <= MIN_QUEUE_SIZE) {
        void generateExerciseBatch(selectedLanguage, selectedLanguageCode, currentLevel);
      }

      return nextExercise;
    },
    [generateExerciseBatch, getQueueKey, takeNextQueuedExercise]
  );

  return useMemo(
    () => ({
      exerciseQueue,
      isGeneratingBatch,
      generateExerciseBatch,
      getNextExercise,
      getQueueKey,
    }),
    [exerciseQueue, generateExerciseBatch, getNextExercise, getQueueKey, isGeneratingBatch]
  );
};
